import { defineStore } from 'pinia';
import type { YouTubeVideo } from '@/types/youtube';

// Define a type for the API response that can be data or an error object
type YouTubeApiResponse = YouTubeVideo[] | { error: string };

export const useYouTubeStore = defineStore('youtube', () => {
  const videos = ref<YouTubeVideo[]>([]);
  const singleVideo = ref<YouTubeVideo | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  
  // Assuming manufacturer might come from a global config or another store eventually
  // For now, let's hardcode it or allow it to be set.
  // const siteStore = useSiteStore(); // Example if you have a site store
  // const manufacturer = computed(() => siteStore.manufacturer);
  const manufacturer = ref('Kia'); // Placeholder, as in the original component

  async function fetchVideos() {
    isLoading.value = true;
    error.value = null;
    try {
      // Call the new server API endpoint
      // Removing explicit generic type due to TS error "Expected 0 type arguments, but got 1"
      const response: YouTubeApiResponse = await $fetch('/api/youtube-videos');
      
      // Check if the server returned an error structure
      if (Array.isArray(response)) {
        videos.value = response;
      } else if (response && typeof response === 'object' && 'error' in response && typeof response.error === 'string') {
        throw new Error(response.error);
      } else {
        // Handle unexpected response structure
        videos.value = []; // Clear videos or handle as appropriate
        throw new Error('Unexpected response structure from server');
      }
    } catch (e: any) {
      error.value = e.message || 'An unknown error occurred while fetching videos from the server.';
      console.error('Error fetching YouTube videos from server API:', e);
    } finally {
      isLoading.value = false;
    }
  }

  function selectVideo(videoId: string) {
    const foundVideo = videos.value.find(v => v.id === videoId);
    singleVideo.value = foundVideo || null;
  }

  function clearSelectedVideo() {
    singleVideo.value = null;
  }

  // The fetchVideos() action should now be called explicitly,
  // e.g., from a page using useAsyncData or from a Nuxt plugin for SSR.
  // Removed automatic initial fetch:
  // if (videos.value.length === 0) {
  //   fetchVideos();
  // }

  return {
    videos,
    singleVideo,
    isLoading,
    error,
    manufacturer,
    fetchVideos,
    selectVideo,
    clearSelectedVideo,
  };
});
